import { useContext, useEffect, useState } from 'react';
import { flushSync } from 'react-dom';

import { Alert, Box, Icon, Image, Text } from '@zillow/constellation-10';
import { IconBuildingFilled, IconDoorOpenFilled } from '@zillow/constellation-icons';

import useMultiplexBuildingSessionId from '../../../hooks/queries/multiplex/useMultiplexBuildingSessionId';
import useMultiplexUnitPublished from '../../../hooks/queries/multiplex/useMultiplexUnitPublished';
import { RMWContext } from '../../../providers/RMWContext';
import { useSelector } from '../../../selectors/useSelector';
import stringHelper from '../../../utils/stringHelper';

import { DEFAULT_ERROR_MESSAGE } from '../../../constants/ErrorMessageConstants';
import { DISPLAY_SERVER_ERROR_AT, EXIT_CTA_LABEL } from '../../../constants/WizardConstants';
import { STEP } from '../../../containers/CreateListingPage/constants';

import { ModularListingContext } from '../../../containers/CreateListingPage/context';
import MultiplexListingNavCard from './components/MultiplexListingNavCard';

import celebrateIllustration from '../../../images/celebrate.svg';

import SpinnerContainer from '../../SpinnerContainer';
import PageWizard from '../../Wizard/PageWizard';
import WizardAlert from '../../WizardAlert';

import type { WizardBodyTemplateProps } from '../../Wizard/types';

const buildMultiplexCreateListingHref = ({
  paths,
  propertyListingAlias,
  multiplexBuildingSessionId,
  step,
}: {
  paths: { propertiesPage: string };
  propertyListingAlias: string;
  multiplexBuildingSessionId: string;
  step: string;
}) => {
  const params = new URLSearchParams({
    multiplexBuildingSessionId,
    step,
  });
  return `${paths.propertiesPage}/${propertyListingAlias}/create-listing?${params.toString()}`;
};

/**
 * TODO: use client-side routing instead of window.location.assign on nextjs migration
 *
 * Full-page navigation: flushSync keeps navigation affordances disabled until unload.
 */
const assignCreateListingLocation = (href: string, setNavigationPending: (pending: boolean) => void) => {
  flushSync(() => {
    setNavigationPending(true);
  });
  window.location.assign(href);
};

const MultiplexListingPublished = ({
  onExitWizard,
  wizardTitle,
  progressStepper,
  totalSteps,
  currentStepIndex,
  showDesktopViewBreakpoint,
  ...rest
}: WizardBodyTemplateProps) => {
  const {
    modularListingState: { serverErrorObj },
  } = useContext(ModularListingContext);

  const { paths } = useContext(RMWContext);

  const listingAlias = useSelector((state) => state.properties.currentProperty.listingAlias);

  const { multiplexBuildingSessionId } = useMultiplexBuildingSessionId();

  const { data, error, isFetching } = useMultiplexUnitPublished({ listingAlias });

  const hasError = Boolean(error);

  const multiplexUnitPublishedErrorMessage = hasError
    ? (error instanceof Error && error.message.trim()) || DEFAULT_ERROR_MESSAGE
    : null;

  const unpublishedCount = data?.unpublishedCount ?? null;
  const hasUnpublishedUnits = unpublishedCount != null && unpublishedCount > 0;

  const publishedUnitNumber = data?.publishedUnitNumber ?? '';
  const nextUnitNumber = data?.nextUnitNumber ?? '';
  const nextUnitAlias = data?.nextUnitAlias ?? '';
  const sessionIdForNavigation = data?.multiplexBuildingSessionId ?? multiplexBuildingSessionId ?? '';

  const showNextUnitCard =
    Boolean(data) && hasUnpublishedUnits && Boolean(nextUnitAlias.trim()) && Boolean(nextUnitNumber.trim());

  const showSpinner = Boolean(listingAlias) && isFetching && !data;
  const showSuccessContent = Boolean(data) && !hasError;
  const canNavigateBack = Boolean(listingAlias?.trim()) && Boolean(sessionIdForNavigation.trim());

  const [isNavigationPending, setIsNavigationPending] = useState(false);

  useEffect(() => {
    setIsNavigationPending(false);
  }, [listingAlias, sessionIdForNavigation, nextUnitAlias]);

  const goToCreateListingStep = (propertyListingAlias: string, step: string) => {
    assignCreateListingLocation(
      buildMultiplexCreateListingHref({
        paths,
        propertyListingAlias,
        multiplexBuildingSessionId: sessionIdForNavigation,
        step,
      }),
      setIsNavigationPending,
    );
  };

  const handleListNextUnitClick = () => {
    if (isNavigationPending || !sessionIdForNavigation || !nextUnitAlias.trim()) {
      return;
    }

    goToCreateListingStep(nextUnitAlias, STEP.REVIEW);
  };

  const handleBackToPropertyClick = () => {
    if (isNavigationPending || !listingAlias || !sessionIdForNavigation) {
      return;
    }

    goToCreateListingStep(listingAlias, STEP.MULTIPLEX_OVERVIEW);
  };

  const unpublishedSetupSentence = hasUnpublishedUnits
    ? `  You have ${stringHelper.pluralize(unpublishedCount ?? 0, 'more unit to setup', 'more units to setup')}.`
    : '';

  return (
    <PageWizard
      {...rest}
      title=""
      wizardTitle={wizardTitle}
      progressStepper={progressStepper}
      totalSteps={totalSteps}
      currentStepIndex={currentStepIndex}
      exitCTALabel={EXIT_CTA_LABEL.SAVE_AND_EXIT}
      onExitTriggered={onExitWizard}
      showDesktopViewBreakpoint={showDesktopViewBreakpoint}
      hideFooter={true}
      displayPremiumTopOfFunnelHeader={false}
    >
      <Box
        css={{
          margin: '0 auto',
          maxWidth: '500px',
          width: '100%',
        }}
        data-testid="MultiplexListingPublished"
      >
        <WizardAlert serverErrorObj={serverErrorObj} displayAt={DISPLAY_SERVER_ERROR_AT.PAGE} />

        {hasError && (
          <Alert data-testid="MultiplexListingPublishedError" tone="critical" css={{ marginBlockEnd: '300' }}>
            {multiplexUnitPublishedErrorMessage}
          </Alert>
        )}

        <Box
          css={{
            display: 'flex',
            flexDirection: 'column',
            gap: '400',
            justifyContent: 'center',
          }}
        >
          {showSpinner && (
            <Box
              css={{
                position: 'relative',
                bottom: '25px',
              }}
            >
              <SpinnerContainer size="lg" height="100px" />
            </Box>
          )}

          {showSuccessContent && (
            <Box
              data-testid="HeaderContainer"
              css={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                gap: '400',
                justifyContent: 'center',
                textAlign: 'center',
              }}
            >
              <Image
                src={celebrateIllustration}
                alt="Celebration illustration"
                css={{ width: '120px', height: '120px', flexShrink: 0, objectFit: 'cover' }}
              />

              <Text textStyle="heading-md">Listing published</Text>

              <Text textStyle="body">
                {publishedUnitNumber &&
                  `Unit ${publishedUnitNumber} will be live on the Zillow Rental Network soon.${unpublishedSetupSentence}`}
              </Text>
            </Box>
          )}

          {(showSuccessContent || (hasError && canNavigateBack)) && (
            <Box
              data-testid="UnitListContainer"
              css={{
                display: 'flex',
                flexDirection: 'column',
                alignSelf: 'stretch',
                gap: '200',
              }}
            >
              <Box css={{ display: 'flex', flexDirection: 'column', gap: '200' }}>
                {showSuccessContent && showNextUnitCard && (
                  <MultiplexListingNavCard
                    data-testid="NextUnitCard"
                    isNavigationPending={isNavigationPending}
                    title={`List unit ${nextUnitNumber} next`}
                    subtitle="Continue setting up your next unit’s listing"
                    leading={
                      <Icon size="lg" css={{ color: 'icon.accent.blue.hero' }} render={<IconDoorOpenFilled />} />
                    }
                    onActivate={handleListNextUnitClick}
                  />
                )}

                {canNavigateBack && (
                  <MultiplexListingNavCard
                    data-testid="BackToPropertyCard"
                    isNavigationPending={isNavigationPending}
                    title="Back to property"
                    subtitle="View all units and manage your property"
                    leading={<Icon size="lg" css={{ color: 'icon.neutral' }} render={<IconBuildingFilled />} />}
                    onActivate={handleBackToPropertyClick}
                  />
                )}
              </Box>
            </Box>
          )}
        </Box>
      </Box>
    </PageWizard>
  );
};

export default MultiplexListingPublished;
